<script lang="ts" setup>
import { FontAwesomeIcon, type FontAwesomeIconProps } from "@fortawesome/vue-fontawesome";
import { faSparkles, faSpinnerThird } from "@fortawesome/pro-light-svg-icons";
import type { Button, PopperOptions, Tooltip, UTooltip } from '#ui/types';
import merge from 'lodash/merge';
import type { Placement } from '@popperjs/core';

export interface ButtonAiProps {
    tooltip?: {
        text?: string;
        popper?: {
            placement?: 'top' | 'bottom' | 'left' | 'right';
        };
    };
    button?: {
        size?: 'xs' | 'sm' | 'md' | 'lg';
        color?: 'white' | 'primary' | 'hd-primary';
        variant?: 'ghost' | 'solid' | 'outline';
    };
    icon?: {
        size?: 'xs' | 'sm' | 'lg' | 'xl';
        color?: string;
    };
}

export interface ButtonAiLoadingStates {
    startLoading: () => void;
    stopLoading: () => void;
}

const props = withDefaults(defineProps<ButtonAiProps>(), {
    tooltip: () => ({
        text: 'Populate fields using AI',
        popper: {
            placement: 'top'
        }
    }),
    button: () => ({
        size: 'md',
        color: 'hd-primary',
        variant: 'ghost'
    }),
    icon: () => ({
        size: 'xl',
        color: 'primary'
    })
});

const tooltip = computed(() => merge({
    text: 'Powered by AI',
    popper: { placement: 'top' },
}, props.tooltip));

const button = computed(() => merge({
    size: 'lg',
    color: 'white',
    variant: 'solid',
    square: true,
}, props.button));

const icon = computed(() => merge({
    icon: faSparkles,
}, props.icon));

const emit = defineEmits<{
    (e: 'clicked', data: ButtonAiLoadingStates): void;
    (e: 'error', error: any): void;
}>();

const loading = ref(false);
const startLoading = () => (loading.value = true);
const stopLoading = () => (loading.value = false);

const onClick = () => {
    try {
        startLoading();
        emit('clicked', { startLoading, stopLoading });
    } catch (error) {
        emit('error', error);
    }
};
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>

<template>
    <UTooltip v-bind="tooltip">
        <UButton
            v-bind="button"
            @click="onClick"
        >
            <slot v-bind="{ loading }">
                <FontAwesomeIcon
                    v-if="!loading"
                    v-bind="icon"
                />
                <FontAwesomeIcon
                    v-else
                    :icon="faSpinnerThird"
                    spin
                />
            </slot>
        </UButton>
    </UTooltip>
</template>
